import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, UserRound, Phone, MapPin, BellRing, HeartHandshake } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CATEGORIES } from "@/lib/activity";
import { rupiah, tanggal } from "@/lib/format";
import { fmtDateTime } from "@/components/portal/SaranAspirasi";

const STATUS = { jamaah: "bg-emerald-100 text-emerald-800", donatur: "bg-[#f6ebd0] text-[#8a6d14]", mustahik: "bg-sky-100 text-sky-800" };
const DSTATUS = { menunggu: "bg-amber-100 text-amber-800", dikonfirmasi: "bg-emerald-100 text-emerald-800", ditolak: "bg-red-100 text-red-800" };
const TYPES = { infaq: "Infaq", sedekah: "Sedekah", zakat: "Zakat", pembangunan: "Dana Pembangunan" };

const Row = ({ label, value, testId }) => (
  <div className="flex gap-3 text-sm py-2 border-b border-slate-100 last:border-0">
    <span className="w-32 shrink-0 text-slate-500">{label}</span>
    <span className="text-slate-800 font-medium whitespace-pre-wrap" data-testid={testId}>{value || "-"}</span>
  </div>
);

export default function JamaahDetail() {
  const { id } = useParams();
  const [j, setJ] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/jamaah/${id}/detail`).then((r) => setJ(r.data)).catch((e) => setError(e?.response?.data?.detail || "Gagal memuat"));
  }, [id]);

  if (error) return <p className="text-red-600" data-testid="jamaah-detail-error">{error}</p>;
  if (!j) return <p className="text-slate-500">Memuat...</p>;

  const total = j.donations.filter((d) => d.status === "dikonfirmasi").reduce((s, d) => s + d.amount, 0);

  return (
    <div data-testid="admin-jamaah-detail-page">
      <PageHeader title="Detail Jamaah" subtitle={j.name}>
        <Button asChild variant="outline" className="rounded-full"><Link to="/admin/jamaah" data-testid="jamaah-detail-back"><ArrowLeft size={15} /> Kembali</Link></Button>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-20 w-20 rounded-full bg-emerald-50 overflow-hidden flex items-center justify-center shrink-0" data-testid="jamaah-detail-avatar">
              {j.photo_path ? <img src={fileUrl(j.photo_path)} alt={j.name} className="h-full w-full object-cover" /> : <UserRound size={30} className="text-emerald-700/50" />}
            </div>
            <div>
              <p className="text-lg font-bold text-emerald-950" data-testid="jamaah-detail-name">{j.name}</p>
              <Badge className={`${STATUS[j.status] || ""} capitalize mt-1`}>{j.status}</Badge>
              {j.has_account && <p className="text-[11px] text-emerald-700 mt-1">Memiliki akun portal</p>}
            </div>
          </div>
          <Row label="Jenis Kelamin" value={j.gender === "P" ? "Perempuan" : "Laki-laki"} />
          <Row label="RT / RW" value={`${j.rt || "-"} / ${j.rw || "-"}`} />
          <Row label="Kontak" value={<span className="inline-flex items-center gap-1"><Phone size={12} /> {j.phone}</span>} testId="jamaah-detail-phone" />
          <Row label="Alamat" value={<span className="inline-flex items-start gap-1"><MapPin size={12} className="mt-1 shrink-0" /> {j.address || "-"}</span>} />
          <Row label="Catatan" value={j.notes} />
          <Row label="Tanggal Daftar" value={fmtDateTime(j.registered_at || j.created_at)} testId="jamaah-detail-registered" />
        </div>
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4"><BellRing size={16} className="text-emerald-800" /><h3 className="font-bold text-emerald-950">Langganan Notifikasi</h3></div>
            {j.subscriptions.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="jamaah-detail-subs-empty">Belum memilih kategori notifikasi.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="jamaah-detail-subs">
                {j.subscriptions.map((k) => <Badge key={k} className="bg-emerald-50 text-emerald-900 border border-emerald-200">{CATEGORIES[k]?.label || k}</Badge>)}
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex items-center justify-between p-6 pb-4">
              <div className="flex items-center gap-2"><HeartHandshake size={16} className="text-emerald-800" /><h3 className="font-bold text-emerald-950">Riwayat Donasi</h3></div>
              <p className="text-sm text-slate-600">Total dikonfirmasi: <b className="text-emerald-950" data-testid="jamaah-detail-total">{rupiah(total)}</b></p>
            </div>
            {j.donations.length === 0 ? (
              <p className="px-6 pb-6 text-sm text-slate-500" data-testid="jamaah-detail-donasi-empty">Belum ada donasi tercatat atas nomor ini.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-emerald-50 text-emerald-900 text-left"><tr><th className="px-6 py-2">Tanggal</th><th className="px-4 py-2">Jenis</th><th className="px-4 py-2">Nominal</th><th className="px-4 py-2">Status</th></tr></thead>
                <tbody>
                  {j.donations.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100" data-testid={`jamaah-detail-donasi-${d.id}`}>
                      <td className="px-6 py-2 text-slate-600">{tanggal(d.date)}</td>
                      <td className="px-4 py-2">{TYPES[d.type] || d.type}</td>
                      <td className="px-4 py-2 font-semibold">{rupiah(d.amount)}</td>
                      <td className="px-4 py-2"><Badge className={`${DSTATUS[d.status] || ""} capitalize`}>{d.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
